import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useActiveMachineTypesForFilterQuery,
  useActiveSitesForFilterQuery,
} from '@/hooks/useReferenceDataQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import { listMachinesRequest } from '@/services/machines';
import type { MachineListItem, MachinesListQuery } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'unallocated' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'unallocated' || s === 'all';
}

function parsePositiveInt(s: string | null): number | undefined {
  if (s === null) return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function MachinesListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialTypeId = parsePositiveInt(searchParams.get('typeId'));
  const initialSiteId = parsePositiveInt(searchParams.get('siteId'));
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [typeId, setTypeId] = useState<number | undefined>(initialTypeId);
  const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (typeId !== undefined) next.set('typeId', String(typeId));
    if (siteId !== undefined) next.set('siteId', String(siteId));
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, typeId, siteId, page, setSearchParams]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, typeId, siteId]);

  const listQueryArgs: MachinesListQuery = useMemo(() => {
    const out: { -readonly [K in keyof MachinesListQuery]: MachinesListQuery[K] } = {
      q: debouncedQ,
      status,
      page,
      pageSize: PAGE_SIZE,
    };
    if (typeId !== undefined) out.typeId = typeId;
    if (siteId !== undefined) out.siteId = siteId;
    return out;
  }, [debouncedQ, status, typeId, siteId, page]);

  const listQuery = useQuery<PaginatedResult<MachineListItem>, ApiError>({
    queryKey: ['machines', listQueryArgs],
    queryFn: () => listMachinesRequest(listQueryArgs),
    staleTime: 30_000,
  });

  // REVIEW FP-004: shared 5-minute-stale reference-data hooks. The previous
  // ad-hoc useQuery here ran at default staleTime: 30s, re-fetching every nav
  // for data that changes once a quarter.
  const machineTypesQuery = useActiveMachineTypesForFilterQuery();
  const sitesQuery = useActiveSitesForFilterQuery();

  const columns = useMemo<ColumnDef<MachineListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/machines/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`machines-row-name-${String(row.original.id)}`}
          >
            {formatMachineName(row.original.name, row.original.vehicleNumber)}
          </Link>
        ),
      },
      {
        accessorKey: 'machineTypeName',
        header: 'Type',
        cell: ({ row }) => <span>{row.original.machineTypeName}</span>,
      },
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) =>
          row.original.siteName === null ? (
            <span
              className="text-muted-foreground"
              data-testid={`machines-row-site-${String(row.original.id)}-empty`}
            >
              —
            </span>
          ) : (
            <span data-testid={`machines-row-site-${String(row.original.id)}`}>
              {row.original.siteName}
            </span>
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as MachineListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('machines.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('machines.create');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Machines</h1>
          <p className="text-muted-foreground">
            Operations roster — every machine, its type, and its current site allocation.
          </p>
        </div>
        {canCreate && (
          <Button asChild data-testid="machines-add-button">
            <Link to="/machines/new">
              <PlusIcon size={16} weight="bold" /> Add Machine
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Machines</CardTitle>
          <CardDescription>
            Search by name or code. Filter by type, site, or status. Click a row to drill in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="machines-search">Search</Label>
              <Input
                id="machines-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Name or code…"
                data-testid="machines-search-input"
              />
            </div>
            <div className="w-48 space-y-1">
              <Label htmlFor="machines-type">Type</Label>
              <Select
                value={typeId !== undefined ? String(typeId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setTypeId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setTypeId(n);
                  }
                }}
              >
                <SelectTrigger id="machines-type" data-testid="machines-type-filter">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="machines-type-filter-all">
                    All types
                  </SelectItem>
                  {(machineTypesQuery.data?.items ?? []).map((t) => (
                    <SelectItem
                      key={t.id}
                      value={String(t.id)}
                      data-testid={`machines-type-filter-${String(t.id)}`}
                    >
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48 space-y-1">
              <Label htmlFor="machines-site">Site</Label>
              <Select
                value={siteId !== undefined ? String(siteId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setSiteId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setSiteId(n);
                  }
                }}
              >
                <SelectTrigger id="machines-site" data-testid="machines-site-filter">
                  <SelectValue placeholder="All sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="machines-site-filter-all">
                    All sites
                  </SelectItem>
                  {(sitesQuery.data?.items ?? []).map((s) => (
                    <SelectItem
                      key={s.id}
                      value={String(s.id)}
                      data-testid={`machines-site-filter-${String(s.id)}`}
                    >
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="machines-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger id="machines-status" data-testid="machines-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="machines-status-active">
                    Active
                  </SelectItem>
                  <SelectItem value="inactive" data-testid="machines-status-inactive">
                    Inactive
                  </SelectItem>
                  <SelectItem value="unallocated" data-testid="machines-status-unallocated">
                    Unallocated
                  </SelectItem>
                  <SelectItem value="all" data-testid="machines-status-all">
                    All
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="machines-loading">
              Loading machines…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="machines-error">
              Failed to load machines.
            </p>
          )}
          {listQuery.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="machines-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="machines-empty">No machines found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`machines-row-${String(row.original.id)}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="machines-pagination-summary"
                >
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="machines-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="machines-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
